/**
 * Which tabs a hub shows, and to whom - FRONTEND.md §9.208, §9.302.
 *
 * The owner asked for *Nastavenia* and *Preklady* to become one sidebar entry each, *"picked from
 * inside, the way Vernostný program already works"* - which is one route with a tab bar, not a
 * section with sub-items. §9.302 then asked for *QR platba* to sit beside the delivery settings the
 * same way.
 *
 * **This lives in `shared` because no module imports another.** `manager-delivery`,
 * `manager-invoicing` and `manager-settings` each depend on exactly `@agos/api-client`,
 * `@agos/shared` and `@agos/ui`, and nothing in any module's source imports `@agos/manager-*`. That
 * flatness is deliberate - modules are siblings the app composes - so a second hub could either copy
 * these five functions or they could move here. They moved. `settings-hub.ts` keeps the tab *lists*,
 * which are its own subject, and re-exports these.
 *
 * **A tab's guard is its route's guard.** The sidebar cannot offer a page the router then refuses
 * (§9.7), and a tab is a sidebar item that has moved indoors. Each hub's own test asserts its tabs'
 * guards equal the routes' - that assertion is what keeps this true, and it belongs beside the tab
 * list rather than here.
 */

export interface TabGuard {
  systemRoles?: readonly string[]
  permission?: string
  /** Legacy's tier gate. An empty string switches it off while keeping the permission gate. */
  franchiseModule?: string
}

export interface HubTab {
  id: string
  label: string
  /** The route this tab draws, kept so a deep link and the tab agree. */
  path: string
  guard: TabGuard
}

export interface TabAccess {
  systemRole: string | undefined
  hasPermission: (permission: string) => boolean
  isModuleEnabled: (key: string) => boolean
}

/**
 * The same three gates `accessRefusal` applies, in the same order.
 *
 * `franchiseModule` is evaluated because `/settings-general` carries one - the drift the settings
 * hub's test caught on its first run, when the tab was declared without it and would have been
 * offered on a franchise whose tier has that module off while the route refused. An **empty string**
 * is legacy's way of switching the tier gate off while keeping the permission gate, and is a value
 * rather than an absence.
 *
 * No `orSystemRoles` and no `superAdminOnly`: no route behind any current tab carries either, and
 * quietly accepting a gate this cannot evaluate would let a future route pass a check that never
 * ran. A hub whose route needs one has to teach this function about it, deliberately.
 */
export function tabIsVisible(tab: HubTab, access: TabAccess): boolean {
  const roles = tab.guard.systemRoles
  if (roles !== undefined && roles.length > 0) {
    if (access.systemRole === undefined || !roles.includes(access.systemRole)) return false
  }
  if (tab.guard.permission !== undefined && !access.hasPermission(tab.guard.permission)) {
    return false
  }
  const tier = tab.guard.franchiseModule
  if (tier !== undefined && tier !== '' && !access.isModuleEnabled(tier)) return false
  return true
}

export function visibleTabs(tabs: readonly HubTab[], access: TabAccess): HubTab[] {
  return tabs.filter((tab) => tabIsVisible(tab, access))
}

/**
 * Which tab to open: the one asked for, if it exists and they may see it.
 *
 * A `?tab=` naming a tab this employee cannot see falls back to their first rather than showing an
 * empty page - a link forwarded by a colleague with more rights than the reader is the normal way
 * that happens, and it is not an error worth a screen of its own.
 */
export function activeTab(tabs: readonly HubTab[], requested: string | null): string | null {
  if (tabs.length === 0) return null
  const asked = tabs.find((tab) => tab.id === requested)
  return asked?.id ?? tabs[0]!.id
}

/**
 * Whether to draw the strip at all.
 *
 * One tab is not a choice. For an employee holding exactly one of a hub's tabs, a strip of one is
 * furniture, and it implies others exist that they cannot have.
 */
export function stripIsWorthDrawing(tabs: readonly HubTab[]): boolean {
  return tabs.length > 1
}
